import React, { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { X, Clock, CalendarDays, Save } from "lucide-react";
import { formatEuro, getTodayIso, emptyableNumber, type Reservation } from "./types";

const inputCls = "w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500 transition-colors";
const labelCls = "text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block";

export function NewReservationModal({
  onClose,
  initialData,
  onSave,
  takenByOptions,
  cashedByOptions,
}: {
  onClose: () => void;
  initialData?: Reservation | null;
  onSave: (reservation: Reservation) => void;
  takenByOptions: string[];
  cashedByOptions: string[];
}) {
  const [lastName, setLastName] = useState(initialData?.lastName ?? "");
  const [firstName, setFirstName] = useState(initialData?.firstName ?? "");
  const [phone, setPhone] = useState(initialData?.phone ?? "");
  const [email, setEmail] = useState(initialData?.email ?? "");
  const [time, setTime] = useState(initialData?.time ?? "20:00");
  const [createdDate, setCreatedDate] = useState(initialData?.createdDate ?? getTodayIso());
  const [paidDate, setPaidDate] = useState(initialData?.paidDate ?? getTodayIso());
  const [adults, setAdults] = useState(initialData?.adults ?? 2);
  const [children, setChildren] = useState(initialData?.children ?? 0);
  const [tarifAdult, setTarifAdult] = useState(initialData?.tarifAdult ?? 98);
  const [tarifChild, setTarifChild] = useState(initialData?.tarifChild ?? 49);
  const [cb, setCb] = useState(initialData?.cb ?? 0);
  const [amex, setAmex] = useState(initialData?.amex ?? 0);
  const [cash, setCash] = useState(initialData?.cash ?? 0);
  const [cheque, setCheque] = useState(initialData?.cheque ?? 0);
  const [zen, setZen] = useState(initialData?.zen ?? 0);
  const [virm, setVirm] = useState(initialData?.virm ?? 0);
  const [comment, setComment] = useState(initialData?.comment ?? "");
  const [tableNumber, setTableNumber] = useState(initialData?.tableNumber ?? "");
  const [takenBy, setTakenBy] = useState(initialData?.takenBy ?? (takenByOptions[0] ?? ""));
  const [cashedBy, setCashedBy] = useState(initialData?.cashedBy ?? (cashedByOptions[0] ?? ""));

  const totalPrice = adults * tarifAdult + children * tarifChild;
  const totalPaid = cb + amex + cash + cheque + zen + virm;
  const remaining = Math.max(totalPrice - totalPaid, 0);

  const handleSave = () => {
    onSave({
      id: initialData?.id ?? Date.now(),
      lastName: lastName.trim(), firstName: firstName.trim(), phone: phone.trim(), email: email.trim(),
      alertSentAt: initialData?.alertSentAt ?? null,
      time, createdDate, paidDate, adults, children, tarifAdult, tarifChild,
      cb, amex, cash, cheque, zen, virm, comment, tableNumber, takenBy, cashedBy,
    });
    onClose();
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.96 }}
          className="bg-surface border border-border rounded-3xl w-full max-w-3xl max-h-[90vh] shadow-2xl flex flex-col overflow-hidden"
        >
          <div className="flex items-center justify-between p-6 border-b border-border shrink-0">
            <h2 className="text-lg font-bold text-content">{initialData ? "Modifier la réservation" : "Nouvelle réservation"}</h2>
            <button onClick={onClose} className="p-2 text-content-muted hover:text-content rounded-xl hover:bg-surface-soft transition-all">
              <X size={20} />
            </button>
          </div>

          <div className="p-6 space-y-5 overflow-y-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className={labelCls}>Nom</label>
                <input type="text" className={inputCls} value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Prénom</label>
                <input type="text" className={inputCls} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Téléphone</label>
                <input type="text" className={inputCls} value={phone} onChange={(e) => setPhone(e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Heure</label>
                <div className="relative">
                  <input type="text" className={inputCls} value={time} onChange={(e) => setTime(e.target.value)} />
                  <Clock size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-content-muted pointer-events-none" />
                </div>
              </div>
            </div>

            <div>
              <label className={labelCls}>Email <span className="normal-case font-normal text-content-muted">(pour l'envoi d'alertes groupées)</span></label>
              <input type="email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="client@exemple.fr" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Date de création</label>
                    <input type="date" className={inputCls} value={createdDate} onChange={(e) => setCreatedDate(e.target.value)} />
                  </div>
                  <div>
                    <label className={labelCls}>Date de paiement</label>
                    <input type="date" className={inputCls} value={paidDate} onChange={(e) => setPaidDate(e.target.value)} />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className={labelCls}>Adultes</label>
                    <input type="number" className={inputCls} value={adults} onChange={(e) => setAdults(Number(e.target.value) || 0)} />
                  </div>
                  <div>
                    <label className={labelCls}>Enfants</label>
                    <input type="number" className={inputCls} value={children} onChange={(e) => setChildren(Number(e.target.value) || 0)} />
                  </div>
                  <div>
                    <label className={labelCls}>Tarif AD.</label>
                    <input type="number" className={inputCls} value={tarifAdult} onChange={(e) => setTarifAdult(Number(e.target.value) || 0)} />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className={labelCls}>Tarif ENF.</label>
                    <input type="number" className={inputCls} value={tarifChild} onChange={(e) => setTarifChild(Number(e.target.value) || 0)} />
                  </div>
                  <div>
                    <label className={labelCls}>Prise par</label>
                    <select className={inputCls} value={takenBy} onChange={(e) => setTakenBy(e.target.value)}>
                      {takenByOptions.map((name) => <option key={name} value={name}>{name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Encaisser par</label>
                    <select className={inputCls} value={cashedBy} onChange={(e) => setCashedBy(e.target.value)}>
                      {cashedByOptions.map((name) => <option key={name} value={name}>{name}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex flex-col">
                <label className={labelCls}>Commentaire</label>
                <textarea
                  className={`${inputCls} flex-1 resize-none`}
                  style={{ minHeight: "150px" }}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className={labelCls}>Numéro de table</label>
              <input type="text" className={inputCls} value={tableNumber} onChange={(e) => setTableNumber(e.target.value)} placeholder="Ex: 1, A1, 5-6" />
            </div>

            <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
              {([
                ["CB", cb, setCb], ["AMEX", amex, setAmex], ["ESPECE", cash, setCash],
                ["CHEQUE", cheque, setCheque], ["ZEN", zen, setZen], ["VIRM", virm, setVirm],
              ] as [string, number, (v: number) => void][]).map(([label, value, setter]) => (
                <div key={label}>
                  <label className={labelCls}>{label} (€)</label>
                  <input type="number" className={inputCls} value={emptyableNumber(value)} onChange={(e) => setter(Number(e.target.value) || 0)} />
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 bg-brand-600 rounded-2xl px-5 py-3 text-brand-fg">
              <div className="flex flex-wrap gap-3 items-center text-sm">
                <span className="font-black">Aperçu paiement :</span>
                <span className="px-3 py-1 rounded-full bg-white/15 border border-white/25 font-medium">Total théorique : {formatEuro(totalPrice)}</span>
                <span className="px-3 py-1 rounded-full bg-white/15 border border-white/25 font-medium">Total payé saisi : {formatEuro(totalPaid)}</span>
              </div>
              <div className="px-4 py-1.5 rounded-full bg-red-500/15 border border-red-300/30 text-red-50 font-bold text-sm">
                Reste à payer : {formatEuro(remaining)}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 p-6 border-t border-border shrink-0">
            <button onClick={onClose} className="px-5 py-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft font-bold text-sm transition-all">
              Annuler
            </button>
            <button onClick={handleSave} className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-sm transition-all">
              <Save size={15} /> Enregistrer
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
